import { create } from 'zustand';
import type { UserPublic } from '../types/auth';

const TOKEN_KEY = 'mike_auth_token';
const USER_KEY = 'mike_auth_user';

export interface AuthState {
  token: string | null;
  user: UserPublic | null;
  isLoggingIn: boolean;
  loginError: string | null;
}

export interface AuthActions {
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  validateSession: () => Promise<void>;
  updateProfile: (data: { name?: string; email?: string }) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
}

function loadToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

function loadUser(): UserPublic | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export const useAuthStore = create<AuthState & AuthActions>()((set, get) => ({
  token: loadToken(),
  user: loadUser(),
  isLoggingIn: false,
  loginError: null,

  login: async (email: string, password: string) => {
    set({ isLoggingIn: true, loginError: null });

    try {
      const resp = await fetch('/api/v1/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.detail || 'Invalid email or password');
      }

      const data = await resp.json();
      localStorage.setItem(TOKEN_KEY, data.token);
      localStorage.setItem(USER_KEY, JSON.stringify(data.user));
      set({ token: data.token, user: data.user, isLoggingIn: false });
    } catch (error) {
      set({
        isLoggingIn: false,
        loginError: error instanceof Error ? error.message : 'Login failed',
      });
    }
  },

  logout: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    window.location.reload();
  },

  validateSession: async () => {
    const { token } = get();
    if (!token) return;

    try {
      const resp = await fetch('/api/v1/auth/me', {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!resp.ok) {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        set({ token: null, user: null });
        return;
      }

      const user = await resp.json();
      localStorage.setItem(USER_KEY, JSON.stringify(user));
      set({ user });
    } catch {
      // Network error — keep existing session, don't force logout
    }
  },

  updateProfile: async (data: { name?: string; email?: string }) => {
    const { token } = get();
    if (!token) throw new Error('Not authenticated');

    const resp = await fetch('/api/v1/auth/profile', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to update profile');
    }

    const user = await resp.json();
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    set({ user });
  },

  changePassword: async (currentPassword: string, newPassword: string) => {
    const { token } = get();
    if (!token) throw new Error('Not authenticated');

    const resp = await fetch('/api/v1/auth/change-password', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ currentPassword, newPassword }),
    });

    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to change password');
    }
  },
}));

/** Returns auth headers for API requests, or empty object if not logged in. */
export function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}
